/**
 * CompanyAreaCard - Branded company content card for the HomeScreen
 * Mirrors the mobile app's ExploreTabCompanyCategorySection:
 * Shows company logo/image with name and "Exclusive content" subtitle
 * Created by claude on 09.03.26
 */
import * as React from 'react';
import { useMemo } from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { useNavigate } from 'react-router-dom';
import { Theme } from '../Theme/Theme';
import { useRootStore } from '../Store/useRootStore';
import { getMediaSmallest } from '../Model/Media/Media';
import { useTrans } from '../Store/System/LocalizationStore';
import { useCompanyRootCategories } from '../Hooks/useExploreCategories';

const CardContainer = styled.div`
  position: relative;
  width: 100%;
  border-radius: 24px;
  overflow: hidden;
  cursor: pointer;
  background: linear-gradient(135deg, ${Theme.Colors.textColor} 0%, ${Theme.Colors.charcoal} 100%);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  margin-bottom: 40px;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.15);

    .company-image {
      transform: scale(1.05);
    }
  }
`;

const CardInner = styled.div`
  display: flex;
  min-height: 280px;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const ImageSection = styled.div`
  flex: 0 0 45%;
  position: relative;
  overflow: hidden;
  min-height: 200px;

  @media (max-width: 768px) {
    flex: none;
    height: 180px;
    min-height: 180px;
  }
`;

const BackgroundImage = styled.div<{ image?: string }>`
  position: absolute;
  inset: 0;
  background: ${({ image }) =>
    image
      ? `url('${image}')`
      : `linear-gradient(135deg, ${Theme.Colors.primary} 0%, ${Theme.Colors.primaryDark} 100%)`};
  background-size: cover;
  background-position: center;
  transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);
`;

const ImageOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, transparent 60%, rgba(46, 46, 46, 0.9) 100%);

  @media (max-width: 768px) {
    background: linear-gradient(180deg, transparent 40%, rgba(46, 46, 46, 0.9) 100%);
  }
`;

const ContentSection = styled.div`
  flex: 1;
  padding: 32px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  color: white;
`;

const CompanyBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  background: ${Theme.Colors.primary};
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 16px;
  width: fit-content;
`;

const LogoImage = styled.img`
  height: 64px;
  width: auto;
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 10;
  border-radius: 12px;
  filter: drop-shadow(0 2px 6px rgba(0,0,0,0.4));
`;

const CompanyName = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0 0 12px 0;
  line-height: 1.3;
  color: white;

  @media (max-width: 600px) {
    font-size: 1.25rem;
  }
`;

const CompanySubtitle = styled.p`
  font-size: 0.95rem;
  opacity: 0.8;
  margin: 0 0 24px 0;
  line-height: 1.6;
`;

const ExploreButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: #00dda2;
  border: none;
  border-radius: 24px;
  color: white;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  width: fit-content;

  &:hover {
    background: #007763;
    transform: translateX(4px);
  }
`;

export type CompanyAreaCardProps = {};

export const CompanyAreaCard: React.FC<CompanyAreaCardProps> = observer(() => {
  const { gymCustomer } = useRootStore();
  const { t } = useTrans();
  const navigate = useNavigate();

  const customer = gymCustomer.customer;
  const gym = gymCustomer.currentGym;

  // Replaced the bespoke useEffect + setState with a proper React Query hook so
  // we get retry + backoff on transient failures, shared cache with other
  // explore-category consumers, and a visible error/loading state for the tile.
  const { data: categories = [] } = useCompanyRootCategories(customer?.gymId);

  // Find the company category the same way the mobile app does:
  // category.identifier === customer.gymId OR category.gymId === customer.gymId
  const companyCategory = useMemo(() => {
    if (!customer?.gymId) return undefined;
    return (
      categories.find((c) => c.identifier === customer.gymId) ??
      categories.find((c) => c.gymId === customer.gymId)
    );
  }, [categories, customer?.gymId]);

  // Get the company image from the category
  const categoryImage = useMemo(() => {
    if (companyCategory && companyCategory.images.length > 0) {
      return getMediaSmallest(companyCategory.images[0]);
    }
    return undefined;
  }, [companyCategory]);

  // Get the gym logo
  const gymLogoUrl = useMemo(() => {
    if (gym?.logo) {
      return getMediaSmallest(gym.logo);
    }
    return undefined;
  }, [gym?.logo]);

  const companyName = companyCategory?.translatedName || gym?.name;

  // Don't render if there's no company association or no company category
  if (!customer?.gymId || !companyCategory) {
    return null;
  }

  const handleClick = () => {
    navigate('/company');
  };

  return (
    <CardContainer onClick={handleClick}>
      {gymLogoUrl && <LogoImage src={gymLogoUrl} alt={companyName} />}
      <CardInner>
        <ImageSection>
          <BackgroundImage
            className="company-image"
            image={categoryImage ?? gymLogoUrl}
          />
          <ImageOverlay />
        </ImageSection>
        <ContentSection>
          <CompanyName>{companyName}</CompanyName>
          <CompanySubtitle>
            {t('home.companyArea.subtitle', { defaultValue: 'Exclusive content for your company' })}
          </CompanySubtitle>
          <ExploreButton>
            {t('home.companyArea.explore', { defaultValue: 'Explore' })} →
          </ExploreButton>
        </ContentSection>
      </CardInner>
    </CardContainer>
  );
});
